import { useEffect, useState, useCallback } from "react";
import { api, downloadFile, formatApiError, queuedMessage } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { Plus, Download, Edit3, X } from "lucide-react";
import { toast, Toaster } from "sonner";

const CATS = [
  { id: "raw_material", label: "Raw Material (BOM)", color: "bg-blue-100 text-blue-700" },
  { id: "buffing", label: "Buffing (WIP)", color: "bg-amber-100 text-amber-700" },
  { id: "finish_goods", label: "Finish Goods", color: "bg-emerald-100 text-emerald-700" },
];
const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyForm = (cat) => ({ date: todayStr(), category: cat, item: "", unit: "pcs", opening_stock: 0, received: 0, consumed: 0, closing_stock: 0, remarks: "" });

export default function Stock() {
  const { user, isSuper } = useAuth();
  const isOp = !isSuper && user?.role !== "admin";
  const [tab, setTab] = useState("raw_material");
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [remark, setRemark] = useState("");
  const [form, setForm] = useState(emptyForm("raw_material"));

  const load = useCallback(() => api.get("/stock", { params: { category: tab } }).then((r) => setItems(r.data)), [tab]);
  useEffect(() => { load(); }, [load]);

  const updateForm = (k, v) => {
    const next = { ...form, [k]: v };
    if (["opening_stock", "received", "consumed"].includes(k)) {
      next.closing_stock = (Number(next.opening_stock) || 0) + (Number(next.received) || 0) - (Number(next.consumed) || 0);
    }
    setForm(next);
  };

  const startNew = () => { setEditing(null); setRemark(""); setForm(emptyForm(tab)); setShowForm(true); };
  const startEdit = (it) => { setEditing(it); setRemark(""); setForm({ ...emptyForm(tab), ...it }); setShowForm(true); };

  const onSave = async () => {
    if (editing && isOp && !remark.trim()) { toast.error("A remark is required to request this change"); return; }
    try {
      const payload = { ...form, category: tab, opening_stock: +form.opening_stock, received: +form.received, consumed: +form.consumed, closing_stock: +form.closing_stock };
      const cfg = isOp && editing ? { params: { remark } } : {};
      let res;
      if (editing) res = await api.put(`/stock/${editing.id}`, payload, cfg);
      else res = await api.post("/stock", payload);
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || (editing ? "Stock entry updated" : "Stock entry added")); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id, rmk) => {
    try {
      const res = await api.delete(`/stock/${id}`, isOp ? { params: { remark: rmk } } : {});
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || "Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout title="Stock Management" subtitle="Opening & closing stock tracker"
      actions={
        <div className="flex gap-2">
          <button data-testid="stock-export-btn" onClick={() => downloadFile("/export/stock", "Stock_Report.xlsx")} className="bg-white border border-slate-300 hover:border-blue-500 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><Download size={16} /> Export</button>
          <button data-testid="stock-add-btn" onClick={startNew} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16} /> Add Entry</button>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <div className="flex gap-2 mb-4">
        {CATS.map((c) => (
          <button key={c.id} data-testid={`stock-tab-${c.id}`}
            onClick={() => setTab(c.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${tab === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-3 px-4">Date</th>
              <th className="text-left py-3 px-4">Item</th>
              <th className="text-right py-3 px-4">Opening</th>
              <th className="text-right py-3 px-4">Received</th>
              <th className="text-right py-3 px-4">Consumed</th>
              <th className="text-right py-3 px-4">Closing</th>
              <th className="text-left py-3 px-4">Unit</th>
              <th className="text-left py-3 px-4">Remarks</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="stock-table-body">
            {items.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-slate-400">No entries. Add your first stock record.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2.5 px-4 font-medium">{it.date}</td>
                <td className="py-2.5 px-4">{it.item}</td>
                <td className="py-2.5 px-4 text-right font-mono">{it.opening_stock}</td>
                <td className="py-2.5 px-4 text-right font-mono text-emerald-600">+{it.received}</td>
                <td className="py-2.5 px-4 text-right font-mono text-red-600">-{it.consumed}</td>
                <td className="py-2.5 px-4 text-right font-mono font-bold">{it.closing_stock}</td>
                <td className="py-2.5 px-4">{it.unit}</td>
                <td className="py-2.5 px-4 text-slate-500 max-w-xs truncate">{it.remarks || "—"}</td>
                <td className="py-2.5 px-4 text-right whitespace-nowrap">
                  <button data-testid={`edit-stock-${it.id}`} onClick={() => startEdit(it)} className="text-slate-400 hover:text-blue-600 p-1"><Edit3 size={16} /></button>
                  <ConfirmDelete testId={`delete-stock-${it.id}`} requireRemark={isOp} onConfirm={(rmk) => onDelete(it.id, rmk)} title="Delete stock entry?" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="font-heading text-xl font-semibold">{editing ? "Edit" : "Stock Entry"} — {CATS.find((c) => c.id === tab).label}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Date"><input data-testid="stock-date" type="date" value={form.date} onChange={(e) => updateForm("date", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Item"><input data-testid="stock-item" value={form.item} onChange={(e) => updateForm("item", e.target.value)} placeholder="e.g. SS 304 Sheet" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Opening Stock"><input data-testid="stock-opening" type="number" value={form.opening_stock} onChange={(e) => updateForm("opening_stock", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Received"><input data-testid="stock-received" type="number" value={form.received} onChange={(e) => updateForm("received", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Consumed"><input data-testid="stock-consumed" type="number" value={form.consumed} onChange={(e) => updateForm("consumed", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Closing Stock (auto)"><input data-testid="stock-closing" type="number" value={form.closing_stock} onChange={(e) => updateForm("closing_stock", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-blue-50 font-semibold" /></Field>
              <Field label="Unit"><input data-testid="stock-unit" value={form.unit} onChange={(e) => updateForm("unit", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Remarks"><input data-testid="stock-remarks" value={form.remarks} onChange={(e) => updateForm("remarks", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              {editing && isOp && (
                <div className="md:col-span-2"><label className="block text-sm font-medium text-amber-700 mb-1.5">Reason for edit (sent to admin for approval)</label><textarea data-testid="stock-approval-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-amber-50" /></div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white">Cancel</button>
              <button data-testid="stock-save-btn" onClick={onSave} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
